import React from 'react';

import PropTypes from 'prop-types';
import { CSSObject } from 'styled-components';

import { useForwardedRef } from '@adsk/alloy-react-helpers';
import { stylePropType } from '@adsk/alloy-react-theme';

import EditButtonGroup from './EditButtonGroup';
import { InputWrapperProps, InputProps } from './types';
import useInlineInput from './useInlineInput';

// role was introduced for v17 and breaks projects using v16 types
// https://github.com/DefinitelyTyped/DefinitelyTyped/pull/53361
type InlineTextAreaInputProps = Omit<
  InputWrapperProps<string>,
  'onChange' | 'role'
> & {
  saveUnmodifiedValue?: boolean;
  cancelOnBlur?: boolean;
  inputProps?: InputProps;
};

/**
 * A TextAreaInput for the InlineEdit component.
 */
const InlineTextAreaInput = React.forwardRef<
  HTMLTextAreaElement,
  InlineTextAreaInputProps
>(
  (
    {
      placeholder,
      value,
      error,
      style,
      id,
      saveUnmodifiedValue = false,
      inputProps,
      cancelOnBlur,
      ...props
    },
    ref,
  ) => {
    const inputRef = useForwardedRef(ref);

    const {
      getInputProps,
      getWrapperProps,
      handleCancel,
      handleSubmit,
      modified,
      inputWrapperTokens,
      inputTokens,
      editButtonTokens,
    } = useInlineInput<HTMLTextAreaElement, string | undefined>({
      isTextArea: true,
      inputRef,
      value,
      saveUnmodifiedValue,
      cancelOnBlur,
      error,
    });

    // To prevent submission with 'Enter' when IME is active (for languages like Japanese or Chinese).
    // keyCode 229 is the IME input key for old browsers
    const isInIMEComposition = (
      e: React.KeyboardEvent<HTMLTextAreaElement>,
    ) => {
      return e.nativeEvent.isComposing || e.keyCode === 229;
    };

    return (
      <div
        {...getWrapperProps({
          style: { height: '100%', ...(style as CSSObject) },
          ...props,
        })}
        css={inputWrapperTokens}
      >
        <textarea
          {...getInputProps({
            style: { height: '100%' },
            placeholder,
            id,
            onKeyDown: (e) => {
              if (
                // Create new line with 'shift + enter'
                // Submit and blur input with 'enter'
                !e.shiftKey &&
                e.key === 'Enter' &&
                !isInIMEComposition(e)
              ) {
                handleSubmit();
              } else if (e.key === 'Escape') {
                handleCancel();
              }
            },
            ...inputProps,
          })}
          css={inputTokens}
        />
        <EditButtonGroup
          handleCancel={handleCancel}
          handleSubmit={handleSubmit}
          modified={modified}
          editButtonTokens={editButtonTokens}
          error={error}
        />
      </div>
    );
  },
);

InlineTextAreaInput.displayName = 'InlineTextAreaInput';

InlineTextAreaInput.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Toggle error state */
  error: PropTypes.bool,
  /** Placeholder text value */
  placeholder: PropTypes.string,
  /** Input id */
  id: PropTypes.string,
  /** The value of the field */
  value: PropTypes.string,
  /** If true the initial value can be saved without being modified, onChange will be fired when saving.
   * By default, the user must modify the value before it can be saved */
  saveUnmodifiedValue: PropTypes.bool,
  /** Additional props to pass to the input element */
  inputProps: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default InlineTextAreaInput;
